import { useEffect } from 'react';
import { useStatus } from '../context/StatusContext';
import { StatusEvaluator } from '../context/StatusEvaluator';
import { getApiBase } from '../utils/apiBase';
import { useDataCache } from '../hooks/useDataCache';
import { FTSO_EVALUATION_TIMEFRAME, FTSO_SUMMARY_HIT_RATE_THRESHOLDS, FTSO_SUBMISSIONS_THRESHOLDS } from '../config/thresholds.js';


const InitialStatusCheck = () => {
    const { updatePageStatus } = useStatus();
    const { getCachedData } = useDataCache();
    const API = getApiBase();
    const checkIndexerStatus = async () => {
      try {
        const response = await fetch(`${API}/indexer_states.json`);
        const data = await response.json();
        const status = StatusEvaluator.indexers(data);
        updatePageStatus('indexers', status);
      } catch (error) {
        console.error('Error checking indexer status:', error);
        // Do not overwrite with red on transient failure; keep last known status
      }
    };
  
    const checkWalletStatus = async () => {
      try {
        const response = await fetch(`${API}/address_balances.json`);
        const data = await response.json();
        const rows = [];
        
        Object.entries(data).forEach(([network, networkData]) => {
          const flareData = networkData.flare;
          const songbirdData = networkData.songbird;
          
          Object.entries(flareData).forEach(([role, flareDetails]) => {
            if (role === 'Delegation') {
              rows.push({
                flareBalance: flareDetails.balance,
                songbirdBalance: null
              });
              rows.push({
                flareBalance: null,
                songbirdBalance: songbirdData[role].balance
              });
            } else {
              rows.push({
                flareBalance: flareDetails.balance,
                songbirdBalance: songbirdData[role].balance
              });
            }
          });
        });
  
        const status = StatusEvaluator.wallets(rows);
        updatePageStatus('balances', status);
      } catch (error) {
        console.error('Error checking wallet status:', error);
        // Keep previous value to avoid transient red/grey flicker
      }
    };
  
    const checkFTSOStatus = async () => {
      try {
        const [summaryResponse, feedCountsResponse, feedConfigResponse, dashboardResponse] = await Promise.all([
          fetch(`${API}/submission_summary.json`),
          fetch(`${API}/feed_counts.json`),
          fetch(`${API}/feed_config.json`),
          fetch(`${API}/ftso_performance_dashboard.json`)
        ]);

        if (!summaryResponse.ok || !feedCountsResponse.ok || !feedConfigResponse.ok || !dashboardResponse.ok) {
          // Incomplete inputs; skip update this cycle to avoid flicker
          return;
        }

        let summaryData, feedCountsData, feedConfigData, dashboardData;
        try {
          summaryData = await summaryResponse.json();
        } catch (e) { throw new Error(`Failed to parse JSON for /submission_summary.json: ${e.message}`); }
        try {
          feedCountsData = await feedCountsResponse.json();
        } catch (e) { throw new Error(`Failed to parse JSON for /feed_counts.json: ${e.message}`); }
        try {
          feedConfigData = await feedConfigResponse.json();
        } catch (e) { throw new Error(`Failed to parse JSON for /feed_config.json: ${e.message}`); }
        try {
          dashboardData = await dashboardResponse.json();
        } catch (e) { throw new Error(`Failed to parse JSON for /ftso_performance_dashboard.json: ${e.message}`); }

        const normalize = (s) => {
          const v = String(s || '').toLowerCase();
          if (v === 'yellow') return 'amber';
          if (v === 'green' || v === 'amber' || v === 'red' || v === 'grey') return v;
          return 'grey';
        };

        const rank = (s) => {
          switch (normalize(s)) {
            case 'green': return 3;
            case 'amber': return 2;
            case 'red': return 1;
            case 'grey':
            default: return 0;
          }
        };

        const evaluateBox = (detailedMetrics) => {
          try {
            const tf = FTSO_EVALUATION_TIMEFRAME;
            const hrObj = detailedMetrics && detailedMetrics.hit_rates && detailedMetrics.hit_rates[tf];
            const rmObj = detailedMetrics && detailedMetrics.results_metrics && detailedMetrics.results_metrics[tf];
            if (!hrObj || !rmObj) return 'grey';

            const hitRate = Number(hrObj.hitRate ?? hrObj.hit_rate ?? hrObj.hitrate);
            const late = Number(rmObj.late || 0);
            const missing = Number(rmObj.missing || 0);
            const invalid = Number(rmObj.invalid || 0);
            const totalLMI = late + missing + invalid;

            let st = 'green';
            if (Number.isFinite(hitRate)) {
              if (hitRate < FTSO_SUMMARY_HIT_RATE_THRESHOLDS.RED_BELOW) st = 'red';
              else if (hitRate < FTSO_SUMMARY_HIT_RATE_THRESHOLDS.AMBER_BELOW) st = 'amber';
            }
            if (totalLMI >= FTSO_SUBMISSIONS_THRESHOLDS.LATE_MISSING_INVALID_COUNT.RED_MIN) st = 'red';
            else if (totalLMI >= FTSO_SUBMISSIONS_THRESHOLDS.LATE_MISSING_INVALID_COUNT.AMBER_MIN && st !== 'red') st = 'amber';
            return st;
          } catch {
            return 'grey';
          }
        };

        const statuses = [];
        try {
          Object.keys(dashboardData || {}).forEach(entityKey => {
            if (entityKey === 'metadata') return;
            Object.keys(dashboardData[entityKey] || {}).forEach(networkKey => {
              const dm = dashboardData?.[entityKey]?.[networkKey]?.detailed_metrics;
              const st = evaluateBox(dm);
              if (st) statuses.push(normalize(st));
            });
          });
        } catch {}

        let overall = 'grey';
        if (statuses.length > 0) {
          overall = statuses.reduce((best, s) => (rank(s) < rank(best) ? s : best), 'green');
        }
        updatePageStatus('ftsoSubmissions', overall);
      } catch (error) {
        console.error('Error checking FTSO status:', error);
        // Keep previous value to avoid transient red/grey flicker
      }
    };
  
    const checkFastUpdatesStatus = async () => {
      try {
        const response = await fetch(`${API}/fast_updates.json`);
        const data = await response.json();
        const status = StatusEvaluator.fastUpdates(data);
        updatePageStatus('fastUpdates', status);
      } catch (error) {
        console.error('Error checking fast updates status:', error);
        // Keep previous value to avoid transient red/grey flicker
      }
    };
  
    const checkNodesStatus = async () => {
    try {
      const cachedHeads = getCachedData('/node_heads.json');
      const cachedNodes = getCachedData('/node_info.json');
      if (cachedHeads && typeof cachedHeads === 'object') {
        const hasUysH = cachedHeads.UYS && typeof cachedHeads.UYS === 'object' && Object.keys(cachedHeads.UYS).length > 0;
        const hasEnvH = cachedHeads.ENV && typeof cachedHeads.ENV === 'object' && Object.keys(cachedHeads.ENV).length > 0;
        if (hasUysH && hasEnvH) {
          const stHeads = StatusEvaluator.nodesWithHeads(cachedHeads, cachedNodes || null);
          if (stHeads) updatePageStatus('nodes', stHeads);
        }
      }

      const [headsResp, nodeResp, extResp] = await Promise.all([
        fetch(`${API}/node_heads.json`),
        fetch(`${API}/node_info.json`),
        fetch(`${API}/external_chain_heights.json`)
      ]);

      if (headsResp.ok) {
        const [headsData, nodeData, externalData] = await Promise.all([
          headsResp.json(),
          nodeResp.ok ? nodeResp.json() : Promise.resolve(null),
          extResp.ok ? extResp.json() : Promise.resolve(null)
        ]);
        const hasUysH2 = headsData && headsData.UYS && typeof headsData.UYS === 'object' && Object.keys(headsData.UYS).length > 0;
        const hasEnvH2 = headsData && headsData.ENV && typeof headsData.ENV === 'object' && Object.keys(headsData.ENV).length > 0;
        if (hasUysH2 && hasEnvH2) {
          const st = StatusEvaluator.nodesWithHeads(headsData, nodeData || null);
          if (st) updatePageStatus('nodes', st);
          return;
        }
        if (nodeResp.ok && nodeData) {
          const hasUys = nodeData && nodeData.UYS && typeof nodeData.UYS === 'object' && Object.keys(nodeData.UYS).length > 0;
          const hasEnv = nodeData && nodeData.ENV && typeof nodeData.ENV === 'object' && Object.keys(nodeData.ENV).length > 0;
          if (hasUys && hasEnv) {
            const st2 = StatusEvaluator.nodes(nodeData, externalData || null);
            updatePageStatus('nodes', st2);
          }
        }
      }
    } catch (error) {
      console.error('Error checking nodes status:', error);
    }
  };

  const checkFdcStatus = async () => {
    try {
      const summaryResp = await fetch(`${API}/fdc_submission_summary.json`);
      if (!summaryResp.ok) return;
      const summaryData = await summaryResp.json();
      const status = StatusEvaluator.fdcSubmissions(summaryData, {});
      updatePageStatus('fdcSubmissions', status);
    } catch (error) {
      console.error('Error checking FDC status:', error);
      // Keep previous value to avoid transient red/grey flicker
    }
  };

  const checkValidatorsStatus = async () => {
    try {
      const response = await fetch(`${API}/validator_info.json`);
      const data = await response.json();
      const status = StatusEvaluator.validators(data);
      updatePageStatus('validators', status);
    } catch (error) {
      console.error('Error checking validators status:', error);
      // Keep previous value to avoid transient red/grey flicker
    }
  };

  const checkRegistrationStatus = async () => {
    try {
      const response = await fetch(`${API}/voter_registration.json`);
      const data = await response.json();
      const status = StatusEvaluator.registration(data);
      updatePageStatus('registration', status);
    } catch (error) {
      console.error('Error checking registration status:', error);
      // Keep previous value to avoid transient red/grey flicker
    }
  };
  
    useEffect(() => {
      checkIndexerStatus();
      checkWalletStatus();
      checkFTSOStatus();
      checkFastUpdatesStatus();
      checkFdcStatus();
      checkNodesStatus();
      checkValidatorsStatus();
      checkRegistrationStatus();
      
      const indexerInterval = setInterval(checkIndexerStatus, 5000);
      const walletInterval = setInterval(checkWalletStatus, 5000);
      const ftsoInterval = setInterval(checkFTSOStatus, 5000);
      const fastUpdatesInterval = setInterval(checkFastUpdatesStatus, 5000);
      const fdcInterval = setInterval(checkFdcStatus, 5000);
      const nodesInterval = setInterval(checkNodesStatus, 5000);
      const validatorsInterval = setInterval(checkValidatorsStatus, 5000);
      const registrationInterval = setInterval(checkRegistrationStatus, 5000);
      
      return () => {
        clearInterval(indexerInterval);
        clearInterval(walletInterval);
        clearInterval(ftsoInterval);
        clearInterval(fastUpdatesInterval);
        clearInterval(fdcInterval);
        clearInterval(nodesInterval);
        clearInterval(validatorsInterval);
        clearInterval(registrationInterval);
      };
    }, []);
  
    return null;
  };

export default InitialStatusCheck;
  
